/**
 * PendingApprovalPage — shown to users who have signed up via Google/Apple
 * (or email) but are waiting for a farm admin to approve their access.
 *
 * The user IS authenticated (has a JWT) but has no tenant assigned yet
 * (or status = 'pending'). They can't use the app until approved.
 */
import { useAuthStore } from '../lib/store'
import { useNavigate } from 'react-router-dom'

const C = {
  bg:    'var(--color-bg)',
  card:  'var(--color-bg-card)',
  border:'rgba(255,255,255,0.08)',
  amber: '#F5A623',
  green: '#2D6A2F',
  text:  'var(--color-text)',
  muted: 'var(--color-text-muted)',
  faint: 'var(--color-text-faint)',
}

function MangoMark({ size = 40 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <ellipse cx="16" cy="19" rx="10" ry="11" fill="#F5A623" />
      <ellipse cx="12" cy="14" rx="3.5" ry="5" fill="rgba(255,255,255,0.18)" transform="rotate(-15 12 14)" />
      <path d="M16 8 C16 8 14 4 18 2" stroke="#2D6A2F" strokeWidth="1.8" strokeLinecap="round" fill="none" />
      <path d="M18 2 C22 1 24 6 20 7 C18 7 17 5 18 2Z" fill="#2D6A2F" />
    </svg>
  )
}

export default function PendingApprovalPage() {
  const { clearAuth } = useAuthStore()
  const navigate = useNavigate()

  function handleSignOut() {
    clearAuth()
    localStorage.removeItem('refresh_token')
    navigate('/login', { replace: true })
  }

  return (
    <main
      id="main-content"
      style={{
        minHeight: '100dvh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', background: C.bg, padding: 24,
      }}
    >
      <div style={{ width: '100%', maxWidth: 440, textAlign: 'center' }}>

        {/* Logo */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
          <MangoMark size={52} />
        </div>

        {/* Card */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 18, padding: '36px 32px' }}>

          {/* Pulse indicator */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
            <div style={{ position: 'relative', width: 56, height: 56 }}>
              <div style={{
                position: 'absolute', inset: 0, borderRadius: '50%',
                background: 'rgba(245,166,35,0.15)',
                animation: 'ping 2s cubic-bezier(0,0,0.2,1) infinite',
              }} />
              <div style={{
                position: 'relative', width: '100%', height: '100%', borderRadius: '50%',
                background: 'rgba(245,166,35,0.2)', border: `2px solid ${C.amber}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 24,
              }}>
                ⏳
              </div>
            </div>
          </div>

          <h1 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: '0 0 10px', fontFamily: '"Syne", system-ui, sans-serif' }}>
            Waiting for Approval
          </h1>
          <p style={{ fontSize: 14, color: C.muted, margin: '0 0 28px', lineHeight: 1.6 }}>
            Your account has been created. A farm admin needs to approve your access before you can get started.
          </p>

          {/* What happens next */}
          <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 12, padding: '18px 20px', marginBottom: 28, textAlign: 'left' }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: C.amber, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 12px' }}>
              What happens next
            </p>
            {[
              { icon: '📧', text: "The farm admin has been notified of your request." },
              { icon: '✅', text: "Once approved, you'll receive a confirmation email." },
              { icon: '🌿', text: "Then you can sign in and start using the farm dashboard." },
            ].map(({ icon, text }) => (
              <div key={text} style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'flex-start' }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>{icon}</span>
                <span style={{ fontSize: 13, color: C.muted, lineHeight: 1.5 }}>{text}</span>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                height: 44, background: C.amber, border: 'none', borderRadius: 10,
                fontSize: 14, fontWeight: 600, color: 'var(--color-bg)', cursor: 'pointer',
              }}
            >
              Check again
            </button>
            <button
              onClick={handleSignOut}
              style={{
                height: 44, background: 'transparent', border: `1px solid ${C.border}`,
                borderRadius: 10, fontSize: 14, color: C.muted, cursor: 'pointer',
              }}
            >
              Sign out
            </button>
          </div>
        </div>

        <p style={{ fontSize: 12, color: C.faint, marginTop: 20, lineHeight: 1.6 }}>
          If you think this is a mistake, ask your farm admin to check the pending requests in{' '}
          <strong style={{ color: C.muted }}>Settings → Team</strong>.
        </p>
      </div>

      {/* Ping animation */}
      <style>{`
        @keyframes ping {
          75%, 100% { transform: scale(2); opacity: 0; }
        }
      `}</style>
    </main>
  )
}
